import { DxDataGrid } from "devextreme-vue/data-grid";
import { cancelButtonProps, saveButtonProps } from "@helpers/button-helper";
import { type ToolbarItem } from "devextreme/ui/popup";
import type { ColumnLookup } from "devextreme/common/grids";
import { useIntl } from "../services/i18n";
import type { DxButton, DxSelectBox } from "devextreme-vue";
import type CustomStore from "devextreme/data/custom_store";
import { reactive, type Ref } from "vue";
import type { ApiStoreOptions } from "@api/integration";

export function makeEditPopupButton(type: "Save" | "Cancel", gridRef: DxDataGrid | undefined): ToolbarItem {
  let opts;
  switch (type) {
    case "Save":
      opts = { ...saveButtonProps(), onClick: () => gridRef?.instance?.saveEditData() };
      break;
    case "Cancel":
      opts = { ...cancelButtonProps(), onClick: () => gridRef?.instance?.cancelEditData() };
      break;
  }
  return {
    widget: 'dxButton',
    toolbar: 'bottom',
    location: 'after',
    options: opts
  }
}

export function currencyFormat() {
  return {
    type: 'currency',
    currency: 'EUR',
    precision: 2
  }
}

export function percentFormat() {
  return {
    type: 'decimal',
    precision: 2,
    formatter: function (value: number) {
      return `${value.toFixed(2)} %`;
    },
    parser: function (e: string) {
      const value = parseFloat(e.replace('%', '').trim());
      return isNaN(value) ? 0 : value;
    }
  }
}

export function customizeTextUppercase(cellInfo: { value?: string | null }) {
  return cellInfo.value?.toUpperCase() ?? "";
}

export function getDefaultGridProperties(): DxDataGrid {

  return {
    width: '100%',
    columnAutoWidth: true,
    rowAlternationEnabled: true,
    pager: {
      showInfo: false,
      showPageSizeSelector: true
    },
    paging: {
      pageSize: 10,
    },
    remoteOperations: {
      filtering: true,
      paging: true,
      sorting: true,
      summary: true,
      grouping: true,
      groupPaging: true
    },
    headerFilter: {
      visible: true
    },
    filterRow: {
      visible: true
    },
    filterPanel: {
      visible: true,
    }
  }
}

export function getVirtualScrollingGridProperties(): DxDataGrid {

  return {
    remoteOperations: true,
    scrolling: {
      mode: 'virtual',
      rowRenderingMode: "standard"
    },
    paging: {
      pageSize: 50,
    }
  }
}

export function getDefaultGridDetailProperties(): DxDataGrid {

  return {
    width: '100%',
    columnAutoWidth: true,
    rowAlternationEnabled: true,
    pager: {
      showInfo: false,
      showPageSizeSelector: true
    },
    paging: {
      pageSize: 10,
    },
    remoteOperations: {
      filtering: true,
      paging: true,
      sorting: true,
      summary: true,
      grouping: true,
      groupPaging: true
    },
    headerFilter: {
      visible: true
    },
    filterRow: {
      visible: true
    },
    filterPanel: {
      visible: false
    }
  }
}

export function getLookup(entity: string, field: string): ColumnLookup {
  const intl = useIntl();
  //@ts-ignore
  const keys = Object.keys(intl.i18n.messages.value[intl.i18n.locale.value][entity][field]);
  let dataSource = keys.map(key => ({
    value: key,
    text: intl.i18n.t(`${entity}.${field}.${key}`)
  })).filter(key => key.value != 'null');

  return {
    dataSource: dataSource,
    displayExpr: 'text',
    valueExpr: 'value'
  }
}

export function getColumnsLookupAsNumber(entity: string, field: string): DxSelectBox {
  const intl = useIntl();
  //@ts-ignore
  const keys = Object.keys(intl.i18n.messages.value[intl.i18n.locale.value][entity][field]);
  let dataSource = keys.map(key => ({
    value: Number(key),
    text: intl.i18n.t(`${entity}.${field}.${key}`)
  }));

  return {
    dataSource: dataSource,
    displayExpr: 'text',
    valueExpr: 'value'
  }
}

export function getSelectBoxDefaultProperties(): Partial<DxSelectBox> {
  return {
    valueExpr: 'Id',
    displayExpr: 'Denominacion',
    searchEnabled: true,
    showClearButton: true
  }
}

export function getRemoteSelectBoxDefaultProperties(): DxSelectBox {
  return {
    dataSource: {
      paginate: true,
      pageSize: 20
    },
    dropDownOptions: {
      maxHeight: 190
    }
  };
}

export function getColumnLookupBoolean(): ColumnLookup {
  const intl = useIntl();
  return {
    dataSource:
      [{
        value: true,
        text: intl.i18n.t(`shared.ui.buttons.yes`)
      },
      {
        value: false,
        text: intl.i18n.t(`shared.ui.buttons.no`)
      }],
    valueExpr: "value",
    displayExpr: "text"
  }
}

export function getDomainEnumSelectBox(entity: string, field: string): DxSelectBox {
  const intl = useIntl();
  //@ts-ignore
  const keys = Object.keys(intl.i18n.messages.value[intl.i18n.locale.value][entity][field]);
  let dataSource = keys.map(key => ({
    value: key,
    text: intl.i18n.t(`${entity}.${field}.${key}`)
  }));

  const selector = {
    ...getSelectBoxDefaultProperties(),
    dataSource: dataSource,
    displayExpr: 'text',
    valueExpr: 'value',
  };

  return selector;
}

export function getRetrocomisionFilter(
  filterValue: any,
  selectedFilterOperation: string,
  dataField: string = "Retrocomision"
) {
  if (filterValue === null || filterValue === undefined) return null;

  const positiveValue = Math.abs(filterValue);

  switch (selectedFilterOperation) {
    case '=':
      return [dataField, '=', positiveValue];
    case '<>':
      return [dataField, '<>', positiveValue];
    case '>':
      return [dataField, '>', positiveValue];
    case '>=':
      return [dataField, '>=', positiveValue];
    case '<':
      return [dataField, '<', positiveValue];
    case '<=':
      return [dataField, '<=', positiveValue];
    default:
      return null;
  }
}

export async function exportGridToExcel(
  component: any,
  fileName: string = 'Export.xlsx',
  worksheetName: string = 'Data',
  autoFilterEnabled: boolean = true
) {
  const { exportDataGrid } = await import('devextreme/excel_exporter');
  const { Workbook } = await import('exceljs');
  const saveAs = (await import('file-saver')).default;

  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet(worksheetName);

  // Exportar los datos del grid al worksheet
  await exportDataGrid({
    component,
    worksheet,
    autoFilterEnabled,
  });

  // Generar el buffer del archivo Excel
  const buffer = await workbook.xlsx.writeBuffer();

  // Guardar el archivo
  saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName);
}

export function getDefaultGridWithStateStoringProperties(storageKey: string, getGridInstanceAction: () => DxDataGrid | undefined): DxDataGrid {
  const intl = useIntl();

  return {
    ...getDefaultGridProperties(),
    stateStoring: {
      enabled: true,
      type: 'localStorage',
      storageKey: storageKey
    },
    columnChooser: {
      enabled: true,
      mode: "select",
      position: {
        my: "right top",
        at: "right bottom",
        of: ".dx-datagrid-column-chooser-button"
      },
      search: {
        enabled: true
      },
      selection: {
        allowSelectAll: true,
        selectByClick: true
      },
      height: "400px",
      width: "400px",
    },
    toolbar: {
      items: [getColumnChooserBtn(), getResetFiltersGridBtn(getGridInstanceAction), getResetStateGridBtn(getGridInstanceAction)]
    },
    pager: {
      visible: true,
      showInfo: true,
      showPageSizeSelector: true,
      infoText: "Registros: {2}",
      allowedPageSizes: [5, 10, 20]
    }
  } as DxDataGrid;
}

export function getColumnChooserBtn(): ToolbarItem {
  const intl = useIntl();

  return {
    name: "columnChooserButton",
    options: {
      hint: intl.i18n.t("shared.ui.buttons.columnChooser"),
      text: "",
      icon: "fa-solid fa-columns-3",
      elementAttr: { class: "grid-toolbar-action-btn" },
      type: "default",
    } as DxButton
  } as ToolbarItem
}

export function getResetStateGridBtn(getGridInstanceAction: () => DxDataGrid | undefined): ToolbarItem {
  const intl = useIntl();

  return {
    location: "after",
    widget: "dxButton",
    options: {
      hint: intl.i18n.t("shared.ui.buttons.gridResetState"),
      icon: "fa-solid fa-arrow-rotate-left",
      type: "default",
      text: "",
      onClick: () => {
        getGridInstanceAction()?.instance?.state(null);
      }
    } as DxButton
  } as ToolbarItem
}

export function getResetFiltersGridBtn(getGridInstanceAction: () => DxDataGrid | undefined): ToolbarItem {
  const intl = useIntl();

  return {
    location: "after",
    widget: "dxButton",
    options: {
      hint: intl.i18n.t("shared.ui.buttons.gridResetFilters"),
      icon: "fa-solid fa-filter-circle-xmark",
      text: "",
      type: "default",
      onClick: () => {
        getGridInstanceAction()?.instance?.clearFilter();
      }
    } as DxButton
  } as ToolbarItem
}
